
import { Buffer } from 'buffer'
import { defineStore } from "pinia"
import { ref } from 'vue'
import { useLoadingStore } from "@/stores/loading"
import { useAppErrorStore } from "@/stores/appError"
import { errorDefaultMessage } from '@/utils/errors'
export { apiIdOf } from '@/utils/resource_ids'
import axios from 'axios'
import {type CancelToken} from 'axios'
export { axiosRetryx } from '@/utils/axios_retryx'
export {
  apiDefaultDescriptions,
  errorDefaultMessage,
  apiMessageOf,
} from '@/utils/errors'

export type ApiOptions = {
  noDisplayLoading?: boolean  
  noInitAppError?: boolean
  noErrorDialog?: boolean
  apiId?: string
  cancelToken?: CancelToken
  displayContactSupportLink?: boolean 
}
export const API_ORIGIN = import.meta.env.VITE_APP_API_ORIGIN
export const OK = 200;
export const WARNING_DISK_CHECK_90 = 250;
export const SITE_UPDATE_REQUIRED_CODE = 490;
export const CHANGED_PERMISSION_CODE = 493;
export const CHANGED_PLAN_CODE = 494;
export const SESSION_EXPIRED_CODE = 501;
export const IP_ADDRESS_BLOCKING_CODE = 401018;
export const KURAEMON_LIVE_ERROR_CODE = 401035;

export const useApiStore = defineStore('api', () => {
  const API_CONFIG = {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }
  const xApiKey = ref<string | null>(null)
  /** x-api-keyの初期化 */
  const initXApiKey = async() => {
    const xorDecode = (base64Encoded: string) => {
      const encoded = Buffer.from(base64Encoded, 'base64').toString();
      const key = 'WjXQbn1C2hU66RFmb6xME5BSadsxK6khBSDjrFby';
      return Array.from(
        encoded,
        (c, i) => String.fromCharCode(c.charCodeAt(0) ^ key.charCodeAt(i % key.length))
      ).join('');
    }
    xApiKey.value = xorDecode(import.meta.env.VITE_APP_ENCRYPTED_X_API_KEY)
  }
  const setXApiKey = (newXApiKey: string | null) => {
    xApiKey.value = newXApiKey
  }

  // API設定を返す.
  const apiConfigFor = async () => {
    if(!xApiKey.value){
      await initXApiKey()
    }
    return {
      ...API_CONFIG,
      headers: {
        ...API_CONFIG.headers,
        'x-api-key': xApiKey.value,
      },
    }
  }

  /**
   * API呼び出し.
   * API実行前後でエラー情報を収集する.
   */
  const apiCall = async <T = any>(apiOptions: ApiOptions, callback: (apiConfig: any) => Promise<T | undefined>) => {
    const loadingStore  = useLoadingStore()
    const appErrorStore  = useAppErrorStore()
    const apiCallProc = async () => {
      try {
        if( !apiOptions.noInitAppError ){
          appErrorStore.updateAppError(null);
        }
        const apiConfig = await apiConfigFor()
    
        return await callback(apiConfig)
      } catch(e: any) {
        // キャンセルの場合、何もしない
        if (apiOptions.cancelToken &&  axios.isCancel(e)) return

        const httpStatus = (e.response ? e.response.status : undefined);
        const sysError = { error: e, httpStatus, description: errorDefaultMessage, ...apiOptions }
        appErrorStore.updateAppError(sysError);
        console.warn('system error caught:', e)
      }
    }

    if( apiOptions.noDisplayLoading ){
      return await apiCallProc()
    }

    return loadingStore.loadingProc(async () => {
      return await apiCallProc()
    })
  }
  /**
   * レスポンスにエラーを含むかどうかを返す.
   * エラーがある場合は、app/appError ステートにエラー情報をセットする.
   */
  const checkResponseError = (
    response: any, 
    descriptions: Array<string>, 
    apiOptions: ApiOptions = {}, okCodes: Array<number> = [OK]) => {
    const appErrorStore  = useAppErrorStore()
    if (!okCodes.includes(response.data.code)) {
      const description = descriptions[response.data.code] || errorDefaultMessage;
      const siteUpdateRequired = (SITE_UPDATE_REQUIRED_CODE === response.data.code);
      const ipAddressBlocking = (IP_ADDRESS_BLOCKING_CODE === response.data.code);
      const sessionExpired = (SESSION_EXPIRED_CODE === response.data.code);
      const changedPermission = (CHANGED_PERMISSION_CODE === response.data.code);
      const changedPlan = (CHANGED_PLAN_CODE === response.data.code);
      
      // displayContactSupportLinkの判定
      let displayContactSupportLink: boolean | undefined = false;
      // 401035エラー（kuraemonLive系）の場合は常にtrue
      if (response.data.code === KURAEMON_LIVE_ERROR_CODE) {
        displayContactSupportLink = true;
      }
      // apiOptionsでdisplayContactSupportLinkが明示的に設定されている場合はそれを優先
      if (Object.prototype.hasOwnProperty.call(apiOptions, 'displayContactSupportLink')) {
        displayContactSupportLink = apiOptions.displayContactSupportLink;
      }

      const apiError = {
        apiResponseCode: response.data.code,
        description,
        siteUpdateRequired,
        ipAddressBlocking,
        sessionExpired,
        changedPermission,
        changedPlan,
        displayContactSupportLink,
        ...apiOptions,
      }

      // 401035エラーの場合は強制的にダイアログを表示する
      if (response.data.code === KURAEMON_LIVE_ERROR_CODE) {
        apiError.noErrorDialog = false;
      }

      appErrorStore.updateAppError(apiError);
      console.warn('API error:', apiError)
      return true;
    }
    return false;
  }
  // cancelTokenSource
  const getCancelTokenSource = ()=>{
    return axios.CancelToken.source()
  }

  return {
    setXApiKey,
    apiConfigFor,
    apiCall,
    checkResponseError,
    getCancelTokenSource,
  }
})
